import React, { useEffect, useState } from 'react'
import { Box, Paper, IconButton, Collapse, Tooltip } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PropTypes from 'prop-types'

/**
 * Collapsible filter panel wrapper.
 *
 * Renders a clickable header (with a chevron indicator) that toggles the
 * visibility of the filter controls below it. The expand/collapse state is
 * persisted in sessionStorage so it survives component remounts (e.g. when the
 * user navigates between tabs on the same screen). State is keyed per screen
 * via the current route path, so each screen keeps its own preference.
 *
 * @param {object} props
 * @param {React.ReactNode} props.title - Header content (label + optional actions).
 * @param {React.ReactNode} props.children - Filter controls.
 * @param {boolean} [props.collapsible=true] - Enable accordion behavior.
 * @param {string} [props.storageKey] - Override the sessionStorage key.
 */
const FilterSection = ({ title, children, collapsible = true, storageKey }) => {
  const persistKey = `filterSection:expanded:${
    storageKey ||
    (typeof window !== 'undefined' ? window.location.pathname : 'default')
  }`

  const [expanded, setExpanded] = useState(() => {
    if (!collapsible) return true
    try {
      const saved = sessionStorage.getItem(persistKey)
      // Default state on load is expanded.
      return saved === null ? true : saved === 'true'
    } catch {
      return true
    }
  })

  useEffect(() => {
    if (!collapsible) return
    try {
      sessionStorage.setItem(persistKey, String(expanded))
    } catch {
      /* sessionStorage unavailable — ignore */
    }
  }, [expanded, collapsible, persistKey])

  const toggle = () => setExpanded(prev => !prev)

  const handleHeaderKeyDown = e => {
    if (!collapsible) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggle()
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        padding: '16px',
        borderBottom: '1px solid var(--border-subtel)',
        borderRadius: '0px',
        backgroundColor: '#fff',
      }}
    >
      <Box
        onClick={collapsible ? toggle : undefined}
        onKeyDown={handleHeaderKeyDown}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? expanded : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          width: 'fit-content',
          cursor: collapsible ? 'pointer' : 'default',
          mb: expanded ? '12px' : 0,
        }}
      >
        {title}

        {collapsible && (
          <Tooltip title={expanded ? 'Collapse filters' : 'Expand filters'}>
            <IconButton
              size="small"
              aria-label={expanded ? 'Collapse filters' : 'Expand filters'}
              aria-expanded={expanded}
              onClick={e => {
                e.stopPropagation()
                toggle()
              }}
              sx={{ color: '#333', p: '2px' }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Collapse in={!collapsible || expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Paper>
  )
}

FilterSection.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node,
  collapsible: PropTypes.bool,
  storageKey: PropTypes.string,
}

export default FilterSection
